import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Select, Upload, Button, message, Row, Col, Image as AntImage, Radio, Card, Space, Divider, Typography, Tag, Popconfirm, Checkbox, Switch } from 'antd';
import { UploadOutlined, PlusOutlined, DeleteOutlined, EditOutlined, CheckCircleOutlined, CodeOutlined, TrophyOutlined, LockOutlined } from '@ant-design/icons';
import PildoraRecompensesModal from '../../pildoresId/content/PildoraRecompensesModal';
import { PostCaza } from '../../../../dao/jutge/PostCaza';
import { GetJavamons } from '../../../../dao/jutge/GetJavamons';
import { GetShopItems } from '../../../../dao/jutge/GetShopItems';
import { GetJavamonConfig, GetSkins } from '../../../../dao/jutge/GetJavamonConfig';
import { PutCazaIntentos } from '../../../../dao/jutge/PutCazaIntentos';
import { GetAllAules } from '../../../../dao/GetAllAules';
import { GetJutgeCazaTests } from '../../../../dao/jutge/GetJutgeCazaTests';
import { PutJutgeCazaTest } from '../../../../dao/jutge/PutJutgeCazaTest';
import { DeleteJutgeCazaTest } from '../../../../dao/jutge/DeleteJutgeCazaTest';
import { BiTrash, BiEdit } from 'react-icons/bi';

export interface StepItem {
    paso: number;
    titulo: string;
    instrucciones: string;
}

interface Props {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    caza?: any; // EscenarioCaza
    defaultConjunt?: number; // pre-link new pildora to this Conjunt de Pildores
    hideAula?: boolean; // when the pildora belongs to a Conjunt, the aula is inherited from the conjunt
    availableCazas?: any[]; // Otras píldoras para seleccionar como bloqueo
}

const NouCazaModal: React.FC<Props> = ({ visible, onClose, onSuccess, caza, defaultConjunt, hideAula, availableCazas }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<any[]>([]); // For background only now
    const [backgroundFileList, setBackgroundFileList] = useState<any[]>([]);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [imageType, setImageType] = useState<string>('random'); // 'random' or 'select'
    const [rewardType, setRewardType] = useState<string>('JAVAMON_RANDOM');
    const [hasIntentosLimit, setHasIntentosLimit] = useState<boolean>(true);
    const [javamons, setJavamons] = useState<any[]>([]);
    const [shopItems, setShopItems] = useState<any[]>([]);
    const [skinsData, setSkinsData] = useState<any>({});
    const [aules, setAules] = useState<any[]>([]);
    const [selectedJavamonId, setSelectedJavamonId] = useState<number | null>(null);
    const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
    const [recompensesModalVisible, setRecompensesModalVisible] = useState(false);
    const [pasos, setPasos] = useState<StepItem[]>([
        { paso: 1, titulo: "Paso 1: Estructura Inicial", instrucciones: "" }
    ]);
    const [tests, setTests] = useState<any[]>([]);
    const [testsLoading, setTestsLoading] = useState(false);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [targetStepOrder, setTargetStepOrder] = useState<number>(1);
    const [editingTest, setEditingTest] = useState<any | null>(null);
    const [testMode, setTestMode] = useState<string>("es");
    const [testSenseEntrada, setTestSenseEntrada] = useState<boolean>(false);
    const [testSubmitting, setTestSubmitting] = useState<boolean>(false);
    const [testForm] = Form.useForm();
    const apiUrl = process.env.REACT_APP_API_URL;

    // Helper to get random image based on ID (fallback)
    const getJavamonImage = (id: number) => {
        const num = (id % 5) + 1; // 1-5
        return `/images/javamon/t1/normal/JM00${num}.png`;
    };

    const getItemImage = (id: number) => {
        return '/images/javamon/shop/unknown_item.png';
    };

    // Helper to parse steps from raw instructions (supports JSON array, "### Paso X", or plain text)
    const parseStepsFromRaw = (raw: string): StepItem[] => {
        if (!raw || !raw.trim()) {
            return [{ paso: 1, titulo: "Paso 1: Estructura Inicial", instrucciones: "" }];
        }
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed.map((p: any, idx: number) => ({
                    paso: p.paso || idx + 1,
                    titulo: p.titulo || `Paso ${idx + 1}`,
                    instrucciones: p.instrucciones || ""
                }));
            }
        } catch (e) {
            // Not JSON, continue to delimiter check
        }

        const stepSplit = raw.split(/(?=###\s*Paso|\bPASO\s+\d+:?)/i).filter(s => s.trim().length > 0);
        if (stepSplit.length > 1) {
            return stepSplit.map((s, idx) => {
                const lines = s.trim().split("\n");
                const title = lines[0].replace(/^[#\s*]+/, "").trim();
                const desc = lines.slice(1).join("\n").trim();
                return {
                    paso: idx + 1,
                    titulo: title || `Paso ${idx + 1}`,
                    instrucciones: desc || s.trim()
                };
            });
        }

        return [{ paso: 1, titulo: "Paso 1: Enunciado Principal", instrucciones: raw.trim() }];
    };

    // Load available aules and reward items
    useEffect(() => {
        if (visible) {
            GetAllAules("").then(data => setAules(data || [])).catch(() => {});
            GetJavamonConfig().then(jConfig => {
                if (jConfig && jConfig.javamons) {
                    setJavamons(typeof jConfig.javamons === 'string' ? JSON.parse(jConfig.javamons) : jConfig.javamons);
                } else {
                    setJavamons([]);
                }
            }).catch(() => {});
            GetSkins().then(data => {
                if (data) setSkinsData(data);
            }).catch(() => {});
            GetShopItems("0").then(data => {
                if (data) setShopItems(data);
            }).catch(() => {});
        }
    }, [visible]);

    // Reset/Populate form on visible/caza change
    useEffect(() => {
        if (visible) {
            form.resetFields();
            if (caza) {
                const initialPasos = parseStepsFromRaw(caza.instrucciones);
                setPasos(initialPasos);

                const hasLimit = caza.intentos_global !== 0 && caza.intentos_global !== null && caza.intentos_global !== undefined;
                setHasIntentosLimit(hasLimit);

                // Map reward type correctly
                let rType = caza.tipo_recompensa || caza.rewardType || 'JAVAMON_RANDOM';
                if (rType === 'javamon') {
                    rType = (caza.javamonId || caza.javamon_config_id) ? 'JAVAMON' : 'JAVAMON_RANDOM';
                } else if (rType === 'item') {
                    // Fallback for legacy data: if reward_valor exists without itemId, it's points; otherwise SHOP_ITEM
                    if ((caza.reward_valor || caza.valor) && !caza.itemId && !caza.id_item) {
                        rType = 'PUNTUACION';
                    } else {
                        rType = 'SHOP_ITEM';
                    }
                }
                setRewardType(rType);

                const itemIdVal = caza.itemId || caza.javamonId || caza.javamon_config_id || caza.id_item || undefined;
                if (itemIdVal) {
                    setSelectedJavamonId(itemIdVal);
                    setSelectedItemId(itemIdVal);
                }

                form.setFieldsValue({
                    nombre: caza.nombre,
                    descripcion: caza.descripcion,
                    dificultad: caza.dificultad,
                    nivel: caza.nivel || 1,
                    vida: caza.vidaJavamon || caza.vida || 100,
                    must: caza.mustInclude && Array.isArray(caza.mustInclude) ? caza.mustInclude.join(',') : caza.mustInclude,
                    mustnot: caza.mustNotInclude && Array.isArray(caza.mustNotInclude) ? caza.mustNotInclude.join(',') : caza.mustNotInclude,
                    estat: caza.estat_config !== undefined ? caza.estat_config : 1,
                    url_teoria: caza.url_teoria || "",
                    id_aula: caza.id_aula || undefined,
                    intentos: hasLimit ? (caza.intentos_global || 5) : 0,
                    reward_tipo: rType,
                    reward_item_id: itemIdVal,
                    reward_valor: caza.reward_valor || caza.valor || undefined,
                    reward_limit_quantity: caza.reward_limit_quantity || caza.limit_quantity || undefined,
                    reward_intentos_min: caza.reward_intentos_min || caza.intentos_min || 1,
                    reward_intentos_max: caza.reward_intentos_max || caza.intentos_max || undefined,
                    reward_time_limit: caza.reward_time_limit || undefined,
                    id_bloqueo: caza.id_bloqueo || undefined
                });
            } else {
                setHasIntentosLimit(true);
                form.setFieldsValue({
                    dificultad: 'Beginner',
                    nivel: 1,
                    vida: 100,
                    estat: 0, // Default 'En edició'
                    intentos: 5,
                    reward_tipo: 'JAVAMON_RANDOM',
                    reward_intentos_min: 1,
                    reward_time_limit: undefined,
                    id_bloqueo: undefined
                });
                setRewardType('JAVAMON_RANDOM');
                setSelectedJavamonId(null);
                setSelectedItemId(null);
            }

            // Load existing background image preview if editing
            if (caza && caza.id) {
                // First try direct fetch from backend endpoint /jutge/caza/{id}/imagen (returns real BLOB as data:image/jpeg;base64,...)
                fetch(`${process.env.REACT_APP_API_URL || 'http://localhost'}:8061/jutge/caza/${caza.id}/imagen`)
                    .then(res => {
                        if (res.ok) return res.json();
                        return null;
                    })
                    .then(data => {
                        if (data && data.background) {
                            setBackgroundFileList([{
                                uid: '-1',
                                name: 'background_actual.png',
                                status: 'done',
                                url: data.background,
                                thumbUrl: data.background
                            }]);
                        } else if (caza.background && typeof caza.background === 'string' && caza.background.trim().length > 0) {
                            let bgUrl = caza.background;
                            if (bgUrl.startsWith('base64:')) {
                                const b64Part = bgUrl.replace(/^base64:[^:]*:/, '');
                                bgUrl = `data:image/png;base64,${b64Part}`;
                            } else if (!bgUrl.startsWith('http') && !bgUrl.startsWith('data:')) {
                                bgUrl = `${process.env.REACT_APP_API_URL || 'http://localhost'}:8061${bgUrl.startsWith('/') ? '' : '/'}${bgUrl}`;
                            }
                            setBackgroundFileList([{
                                uid: '-1',
                                name: 'background_actual.png',
                                status: 'done',
                                url: bgUrl,
                                thumbUrl: bgUrl
                            }]);
                        } else {
                            setBackgroundFileList([]);
                        }
                    })
                    .catch(() => {
                        if (caza.background && typeof caza.background === 'string' && caza.background.trim().length > 0) {
                            let bgUrl = caza.background;
                            if (bgUrl.startsWith('base64:')) {
                                const b64Part = bgUrl.replace(/^base64:[^:]*:/, '');
                                bgUrl = `data:image/png;base64,${b64Part}`;
                            } else if (!bgUrl.startsWith('http') && !bgUrl.startsWith('data:')) {
                                bgUrl = `${process.env.REACT_APP_API_URL || 'http://localhost'}:8061${bgUrl.startsWith('/') ? '' : '/'}${bgUrl}`;
                            }
                            setBackgroundFileList([{
                                uid: '-1',
                                name: 'background_actual.png',
                                status: 'done',
                                url: bgUrl,
                                thumbUrl: bgUrl
                            }]);
                        } else {
                            setBackgroundFileList([]);
                        }
                    });
            } else {
                setBackgroundFileList([]);
            }

            if (caza && caza.id) {
                fetchTests(caza.id);
            } else {
                setTests([]);
            }
        }
    }, [visible, caza, form]);

    const fetchTests = async (idCaza: string | number) => {
        setTestsLoading(true);
        try {
            const data = await GetJutgeCazaTests(String(idCaza));
            setTests(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Error fetching tests for caza:", e);
        } finally {
            setTestsLoading(false);
        }
    };

    const handleOpenAddTest = (stepNumber: number) => {
        if (!caza || !caza.id) {
            message.info("Guarda la píldora primero para poder asociarle pruebas unitarias al paso.");
            return;
        }
        setEditingTest(null);
        setTargetStepOrder(stepNumber);
        setTestMode("es");
        setTestSenseEntrada(false);
        testForm.resetFields();
        testForm.setFieldsValue({ ordre: stepNumber });
        setTestModalOpen(true);
    };

    const handleOpenEditTest = (test: any) => {
        setEditingTest(test);
        setTargetStepOrder(test.ordre || 1);
        setTestMode(test.tipus || "es");
        setTestSenseEntrada(false);
        testForm.resetFields();

        try {
            const parts = (test.fitxer || "").split(":");
            if (test.tipus === "es") {
                const entrada = parts[1] ? atob(parts[1]) : "";
                const sortida = parts[2] ? atob(parts[2]) : "";
                testForm.setFieldsValue({
                    ordre: test.ordre || 1,
                    entrada: entrada,
                    sortida: sortida,
                    name: test.name
                });
                setTestSenseEntrada(entrada === "");
            } else if (test.tipus === "method" || test.tipus === "methodtest") {
                const json = parts[2] ? JSON.parse(atob(parts[2])) : {};
                testForm.setFieldsValue({
                    ordre: test.ordre || 1,
                    methodName: json.methodName,
                    modifiers: json.modifiers,
                    returnType: json.returnType,
                    parameters: json.parameters,
                    entrada: json.entrada,
                    sortida: json.sortida,
                    name: test.name
                });
            } else if (test.tipus === "content") {
                const json = parts[2] ? JSON.parse(atob(parts[2])) : {};
                testForm.setFieldsValue({
                    ordre: test.ordre || 1,
                    contentText: json.contentText,
                    mustContain: json.mustContain !== undefined ? json.mustContain : true,
                    name: test.name
                });
            }
        } catch (e) {
            console.error("Error decoding test data:", e);
        }

        setTestModalOpen(true);
    };

    const handleDeleteTest = async (testId: number) => {
        setTestsLoading(true);
        const ok = await DeleteJutgeCazaTest(testId);
        if (ok) {
            message.success("Test eliminado correctamente");
            if (caza && caza.id) {
                await fetchTests(caza.id);
            }
        } else {
            message.error("No se pudo eliminar el test");
        }
        setTestsLoading(false);
    };

    const handleMoveTest = async (test: any, newOrdre: number) => {
        if (!caza || !caza.id || (test.ordre || 1) === newOrdre) return;
        setTestsLoading(true);
        const payload: any = {
            id: test.id,
            idCaza: parseInt(caza.id),
            tipus: test.tipus,
            ordre: newOrdre,
            fitxer: test.fitxer,
            name: test.name
        };
        const success = await PutJutgeCazaTest(String(caza.id), payload);
        if (success) {
            message.success(`Test movido al Paso ${newOrdre}`);
            await fetchTests(caza.id);
        } else {
            message.error("No se pudo mover el test");
        }
        setTestsLoading(false);
    };

    const handleSaveTest = async (values: any) => {
        if (!caza || !caza.id) return;
        setTestSubmitting(true);

        const ordre = values.ordre !== undefined ? Number(values.ordre) : targetStepOrder;
        let fitxer = "";
        let name = values.name || values.methodName || `Paso ${ordre} - Test`;

        if (testMode === "es") {
            const entrada = values.entrada || "";
            const sortida = values.sortida || "";
            fitxer = `ES:${btoa(entrada)}:${btoa(sortida)}`;
        } else if (testMode === "method" || testMode === "methodtest") {
            const json = JSON.stringify({
                methodName: values.methodName,
                modifiers: values.modifiers || [],
                returnType: values.returnType,
                parameters: values.parameters || [],
                entrada: values.entrada || "",
                sortida: values.sortida || ""
            });
            fitxer = `METHOD:DATA:${btoa(json)}`;
        } else if (testMode === "content") {
            const json = JSON.stringify({
                contentText: values.contentText,
                mustContain: values.mustContain !== undefined ? values.mustContain : true
            });
            fitxer = `CONTENT:DATA:${btoa(json)}`;
            name = name || "Content Check";
        }

        const payload: any = {
            idCaza: parseInt(caza.id),
            tipus: testMode,
            ordre: ordre,
            fitxer: fitxer,
            name: name
        };

        if (editingTest && editingTest.id) {
            payload.id = editingTest.id;
        }

        const success = await PutJutgeCazaTest(String(caza.id), payload);
        if (success) {
            message.success(editingTest ? "Test actualizado" : "Test creado con éxito");
            setTestModalOpen(false);
            setEditingTest(null);
            testForm.resetFields();
            await fetchTests(caza.id);
        } else {
            message.error("Error al guardar el test");
        }
        setTestSubmitting(false);
    };

    const testModifierOptions = ["public", "private", "protected", "static", "final", "abstract"];
    const testTypeOptions = [
        { value: "byte", label: "byte" },
        { value: "int", label: "int" },
        { value: "long", label: "long" },
        { value: "double", label: "double" },
        { value: "boolean", label: "boolean" },
        { value: "String", label: "String" },
        { value: "void", label: "void" },
        { value: "int[]", label: "int[]" },
        { value: "String[]", label: "String[]" }
    ];

    const compressImage = async (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image(); // HTMLImageElement
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const MAX_WIDTH = 800;
                    const MAX_HEIGHT = 800;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        if (blob) {
                            const compressedFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now(),
                            });
                            resolve(compressedFile);
                        } else {
                            reject(new Error("Canvas blob error"));
                        }
                    }, 'image/jpeg', 0.7);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleBackgroundChange = ({ fileList }: any) => {
        setBackgroundFileList(fileList);
    };

    const onFinish = async (values: any) => {
        const isNew = !caza;

        let backgroundFileToUpload = null;
        if (backgroundFileList.length > 0 && backgroundFileList[0].originFileObj) {
            backgroundFileToUpload = backgroundFileList[0].originFileObj;
            if (backgroundFileToUpload.type && backgroundFileToUpload.type.startsWith('image/')) {
                try {
                    backgroundFileToUpload = await compressImage(backgroundFileToUpload);
                } catch (error) {
                    console.error("Error compressing background", error);
                }
            }
        }

        // Serialize steps into instrucciones (valid JSON array)
        const serialisedInstrucciones = JSON.stringify(pasos.map((p, idx) => ({
            paso: idx + 1,
            titulo: p.titulo.trim() || `Paso ${idx + 1}`,
            instrucciones: p.instrucciones.trim()
        })));

        const selectedRewardType = values.reward_tipo || rewardType || 'JAVAMON_RANDOM';
        const selectedIdItem = (selectedRewardType === 'PUNTUACION' || selectedRewardType === 'JAVAMON_RANDOM') 
            ? null 
            : (values.reward_item_id || null);

        // Support all reward types: PUNTUACION, JAVAMON_RANDOM, JAVAMON, SHOP_ITEM, SKIN, ARMA, ESCUDO, etc.
        const isJavamonType = selectedRewardType === 'JAVAMON' || selectedRewardType === 'JAVAMON_RANDOM';
        const javamonId = selectedRewardType === 'JAVAMON' ? (selectedIdItem || 0) : 0;
        const itemId = (!isJavamonType && selectedRewardType !== 'PUNTUACION') ? (selectedIdItem || 0) : 0;

        const json = {
            id: caza ? caza.id : 0,
            nombre: values.nombre,
            descripcion: values.descripcion,
            dificultad: values.dificultad,
            nivel: 1, // Ignore nivel from form
            instrucciones: serialisedInstrucciones,
            vida: 100, // Ignore vida from form
            must: values.must || "",
            mustnot: values.mustnot || "",
            randomJavamon: selectedRewardType === 'JAVAMON_RANDOM',
            javamonId: javamonId,
            itemId: itemId,
            rewardType: selectedRewardType,
            tipo_recompensa: selectedRewardType,
            id_item: selectedIdItem,
            reward_valor: values.reward_valor || null,
            reward_limit_quantity: values.reward_limit_quantity || null,
            reward_intentos_min: values.reward_intentos_min || 1,
            reward_intentos_max: values.reward_intentos_max || null,
            reward_time_limit: values.reward_time_limit || null,
            estat: values.estat !== undefined ? values.estat : 1,
            id_aula: values.id_aula || null,
            url_teoria: values.url_teoria || "",
            intentos: hasIntentosLimit ? (Number(values.intentos) || 5) : 0,
            id_bloqueo: values.id_bloqueo || null,
            id_conjunt: caza?.id_conjunt ?? defaultConjunt ?? null
        };

        const formData = new FormData();
        // Javamon file no longer sent
        if (backgroundFileToUpload) {
            formData.append("background", backgroundFileToUpload);
        }
        formData.append("json", JSON.stringify(json));

        const result = await PostCaza(formData);
        if (result && result.ok) {
            const cazaId = (caza && caza.id) ? caza.id : result.id;
            // Update attempts limit if we have an ID
            if (cazaId) {
                const targetIntentos = hasIntentosLimit ? (Number(values.intentos) || 5) : 0;
                await PutCazaIntentos(String(cazaId), "0", targetIntentos);
            }

            message.success(isNew ? "Píldora creada correctamente" : "Píldora actualizada correctamente");
            form.resetFields();
            setFileList([]);
            setBackgroundFileList([]);
            setPreviewImage(null);
            onSuccess();
        } else {
            message.error("Error al guardar la Píldora");
        }
    };

    const handleAddPaso = () => {
        const nextNum = pasos.length + 1;
        setPasos(prev => [
            ...prev,
            { paso: nextNum, titulo: `Paso ${nextNum}: `, instrucciones: "" }
        ]);
    };

    const handleRemovePaso = (index: number) => {
        if (pasos.length <= 1) {
            message.warning("Debe haber al menos un paso en la píldora.");
            return;
        }
        setPasos(prev => {
            const filtered = prev.filter((_, i) => i !== index);
            return filtered.map((p, idx) => ({ ...p, paso: idx + 1 }));
        });
    };

    const handlePasoChange = (index: number, field: 'titulo' | 'instrucciones', value: string) => {
        setPasos(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const isLinked = caza && (caza.id_origen || (caza.linked_aules_count && caza.linked_aules_count > 1));

    return (
        <Modal
            title={
                <Space align="center">
                    <span>{caza ? "Editar Píldora de Aprendizaje" : "Nueva Píldora de Aprendizaje"}</span>
                    {isLinked && (
                        <Tag color="purple" style={{ fontWeight: 'normal' }}>
                            🔗 Compartida en {caza.linked_aules_count || 'múltiples'} aulas
                        </Tag>
                    )}
                </Space>
            }
            open={visible}
            onCancel={onClose}
            maskClosable={false}
            width={950} // Wider modal for step builder
            footer={null}
        >
            {isLinked && (
                <div style={{
                    marginBottom: 16,
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: '#f9f0ff',
                    border: '1px solid #d3adf7',
                    color: '#531dab',
                    fontSize: '0.85rem'
                }}>
                    ℹ️ <strong>Píldora común:</strong> El contenido (enunciado, pasos, teoría, configuración) y los tests están sincronizados entre todas las aulas donde esté asignada esta píldora. Las entregas de los estudiantes de cada aula se mantienen independientes.
                </div>
            )}
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ dificultad: 'Beginner', estat: 0 }}
            >
                <Row gutter={24}>
                    {/* Left Column: Basic Info & Attempts */}
                    <Col span={12}>
                        <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name="descripcion" label="Descripción" rules={[{ required: true }]}>
                            <Input.TextArea rows={2} />
                        </Form.Item>
                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item name="dificultad" label="Dificultad">
                                    <Select>
                                        <Select.Option value="Beginner">Beginner</Select.Option>
                                        <Select.Option value="Intermediate">Intermediate</Select.Option>
                                        <Select.Option value="Expert">Expert</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="estat" label="Estat">
                                    <Select>
                                        <Select.Option value={0}>En Edició (0)</Select.Option>
                                        <Select.Option value={1}>Visible (1)</Select.Option>
                                        <Select.Option value={2}>Finalitzat (2)</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Configuración de Intentos (Habilitar / Deshabilitar) */}
                        <Card size="small" style={{ marginBottom: 16, background: '#f8fafc', borderColor: '#e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: hasIntentosLimit ? 10 : 0 }}>
                                <div>
                                    <span style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Límite de Intentos</span>
                                    <div style={{ fontSize: 11, color: '#64748b' }}>
                                        {hasIntentosLimit ? "Número máximo de intentos para los alumnos" : "Intentos ilimitados activados"}
                                    </div>
                                </div>
                                <Switch
                                    checked={hasIntentosLimit}
                                    onChange={(checked) => setHasIntentosLimit(checked)}
                                    checkedChildren="Con límite"
                                    unCheckedChildren="Ilimitados"
                                />
                            </div>
                            {hasIntentosLimit && (
                                <Form.Item
                                    name="intentos"
                                    label="Número de Intentos Permitidos"
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Indica el número de intentos' }]}
                                >
                                    <InputNumber min={1} max={999} style={{ width: '100%' }} />
                                </Form.Item>
                            )}
                        </Card>

                        <Form.Item
                            name="url_teoria"
                            label="URL Teoría / Guía (Múltiples URLs separadas por coma o salto de línea)"
                            tooltip="Puedes indicar varias URLs de documentación/teoría; en la vista del alumno se podrán consultar mediante pestañas."
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder="https://mi-usuario.gitlab.io/tema1, https://mi-usuario.gitlab.io/tema2..."
                            />
                        </Form.Item>

                        {!hideAula && (
                            <Form.Item name="id_aula" label="Aula / Clase Asignada">
                                <Select placeholder="Selecciona una aula (opcional)" allowClear>
                                    {aules.map((aula: any) => (
                                        <Select.Option key={aula.id} value={aula.id}>
                                            {aula.nom}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}

                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, curr) => prev.id_aula !== curr.id_aula}
                        >
                            {({ getFieldValue }) => {
                                const currentAulaId = getFieldValue('id_aula') ?? caza?.id_aula;
                                const filteredCazas = (availableCazas || []).filter((c: any) => {
                                    if (c.id === caza?.id) return false;
                                    // Si la píldora actual o la otra tienen aula asignada, deben coincidir
                                    if (currentAulaId !== undefined && currentAulaId !== null && c.id_aula !== undefined && c.id_aula !== null) {
                                        return String(c.id_aula) === String(currentAulaId);
                                    }
                                    return true;
                                });

                                return (
                                    <Form.Item
                                        name="id_bloqueo"
                                        label={
                                            <Space>
                                                <LockOutlined style={{ color: '#fa8c16' }} />
                                                <span>Bloqueo de acceso (Prerrequisito)</span>
                                            </Space>
                                        }
                                        tooltip="En modo desafío y modo juego, no se podrá acceder a esta píldora hasta que el alumno haya intentado al menos una vez la píldora indicada."
                                    >
                                        <Select
                                            allowClear
                                            placeholder="Sin bloqueo (Acceso libre)"
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option: any) => {
                                                const text = Array.isArray(option?.children)
                                                    ? option.children.join(' ')
                                                    : String(option?.children ?? option?.label ?? '');
                                                return text.toLowerCase().includes(input.toLowerCase());
                                            }}
                                        >
                                            {filteredCazas.map((c: any) => (
                                                <Select.Option key={c.id} value={c.id}>
                                                    🔒 {c.nombre} {c.aula ? `(${c.aula})` : ''}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>

                        <Form.Item label="Imagen de Fondo (Background)">
                            <Upload
                                beforeUpload={() => false}
                                maxCount={1}
                                fileList={backgroundFileList}
                                onChange={handleBackgroundChange}
                                listType="picture"
                                onPreview={(file) => {
                                    const src = file.url || file.thumbUrl;
                                    if (src) {
                                        window.open(src, '_blank');
                                    }
                                }}
                            >
                                {backgroundFileList.length === 0 && (
                                    <Button icon={<UploadOutlined />}>Subir Fondo</Button>
                                )}
                            </Upload>
                        </Form.Item>
                    </Col>

                    {/* Right Column: Code Conditions & Rewards (Same as ActivitatsIdExercicis.tsx) */}
                    <Col span={12}>
                        <Form.Item name="must" label="Must Include (separado por comas)">
                            <Input placeholder="for,if,..." />
                        </Form.Item>
                        <Form.Item name="mustnot" label="Must Not Include (separado por comas)">
                            <Input placeholder="while,..." />
                        </Form.Item>

                        <Card 
                            size="small" 
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, color: '#0284c7' }}>🎁 Recompensas de la Píldora</span>
                                    {caza && caza.id ? (
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<TrophyOutlined />}
                                            style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                                            onClick={() => setRecompensesModalVisible(true)}
                                        >
                                            Configurar Recompensas
                                        </Button>
                                    ) : null}
                                </div>
                            } 
                            style={{ marginBottom: 16 }}
                        >
                            {caza && caza.id ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <Typography.Text type="secondary" style={{ fontSize: '0.85rem' }}>
                                        Gestiona múltiples recompensas (Javamons, monedas, armas, skins, items...) con condiciones de intentos y tiempo de forma personalizada.
                                    </Typography.Text>
                                    <Button
                                        type="dashed"
                                        block
                                        icon={<TrophyOutlined />}
                                        style={{ color: '#722ed1', borderColor: '#d3adf7' }}
                                        onClick={() => setRecompensesModalVisible(true)}
                                    >
                                        Abrir Selector de Recompensas
                                    </Button>
                                </div>
                            ) : (
                                <div style={{ padding: '8px 0', textAlign: 'center' }}>
                                    <Typography.Text type="secondary" style={{ fontSize: '0.85rem' }}>
                                        💡 Guarda la píldora primero para poder configurar sus recompensas personalizadas y múltiples.
                                    </Typography.Text>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>


                {/* Full-width Multi-Step Builder Section */}
                <Divider style={{ margin: "24px 0 16px 0" }} />

                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                            <Typography.Title level={5} style={{ margin: 0, color: '#0284c7' }}>
                                📌 Fases / Pasos de Aprendizaje ({pasos.length} {pasos.length === 1 ? 'paso' : 'pasos'})
                            </Typography.Title>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Configura cada tramo del ejercicio. En cada paso el alumno completará un objetivo y se ejecutarán sus tests asociados.
                            </Typography.Text>
                        </div>
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={handleAddPaso}
                            style={{ borderColor: '#0284c7', color: '#0284c7', fontWeight: 600 }}
                        >
                            Añadir Paso
                        </Button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {pasos.map((p, idx) => (
                            <Card
                                key={idx}
                                size="small"
                                style={{
                                    border: '1px solid #bae6fd',
                                    borderRadius: 10,
                                    background: '#f0f9ff',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
                                }}
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{
                                            background: '#0284c7',
                                            color: '#fff',
                                            borderRadius: '50%',
                                            width: 22,
                                            height: 22,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 12,
                                            fontWeight: 'bold'
                                        }}>
                                            {idx + 1}
                                        </span>
                                        <span style={{ fontWeight: 600, color: '#0369a1' }}>Paso {idx + 1}</span>
                                    </div>
                                }
                                extra={
                                    pasos.length > 1 && (
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleRemovePaso(idx)}
                                            size="small"
                                        >
                                            Eliminar Paso
                                        </Button>
                                    )
                                }
                            >
                                <Row gutter={16}>
                                    <Col span={24} style={{ marginBottom: 8 }}>
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                                            Título del Paso:
                                        </label>
                                        <Input
                                            placeholder={`Ej: Paso ${idx + 1}: Definición de variables`}
                                            value={p.titulo}
                                            onChange={(e) => handlePasoChange(idx, 'titulo', e.target.value)}
                                        />
                                    </Col>
                                    <Col span={24}>
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                                            Instrucciones para este paso:
                                        </label>
                                        <Input.TextArea
                                            rows={3}
                                            placeholder="Detalla qué debe implementar el alumno en esta fase..."
                                            value={p.instrucciones}
                                            onChange={(e) => handlePasoChange(idx, 'instrucciones', e.target.value)}
                                        />
                                    </Col>
                                </Row>

                                {/* Tests configured for this step */}
                                <div style={{
                                    marginTop: 14,
                                    padding: '10px 14px',
                                    background: '#ffffff',
                                    borderRadius: 8,
                                    border: '1px dashed #7dd3fc'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <CodeOutlined style={{ color: '#0284c7' }} />
                                            <span style={{ fontSize: 12, fontWeight: 700, color: '#0369a1' }}>
                                                Tests Unitarios de Validación del Paso {idx + 1}
                                            </span>
                                            <Tag color="cyan" style={{ borderRadius: 10, fontSize: 11 }}>
                                                {tests.filter(t => (t.ordre || 1) === (idx + 1)).length} tests
                                            </Tag>
                                        </div>

                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => handleOpenAddTest(idx + 1)}
                                            style={{ background: '#0284c7', borderColor: '#0284c7', fontSize: 11 }}
                                        >
                                            + Añadir Test a este Paso
                                        </Button>
                                    </div>

                                    {/* Test items list */}
                                    {tests.filter(t => (t.ordre || 1) === (idx + 1)).length === 0 ? (
                                        <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '4px 0' }}>
                                            No hay tests configurados para este paso. Haz clic en "+ Añadir Test a este Paso" para agregarlos.
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {tests.filter(t => (t.ordre || 1) === (idx + 1)).map(test => {
                                                let summary = test.name || "Test";
                                                let details = "";
                                                try {
                                                    const parts = (test.fitxer || "").split(":");
                                                    if (test.tipus === "es") {
                                                        const entrada = parts[1] ? atob(parts[1]) : "";
                                                        const sortida = parts[2] ? atob(parts[2]) : "";
                                                        details = `In: "${entrada.substring(0, 35)}${entrada.length > 35 ? '...' : ''}" -> Out: "${sortida.substring(0, 35)}${sortida.length > 35 ? '...' : ''}"`;
                                                    } else if (test.tipus === "method" || test.tipus === "methodtest") {
                                                        const json = parts[2] ? JSON.parse(atob(parts[2])) : {};
                                                        details = `Método: ${json.returnType || 'void'} ${json.methodName || ''}(...)`;
                                                    } else if (test.tipus === "content") {
                                                        const json = parts[2] ? JSON.parse(atob(parts[2])) : {};
                                                        details = `Contiene: "${json.contentText || ''}"`;
                                                    }
                                                } catch (e) {
                                                    details = test.fitxer || "";
                                                }

                                                return (
                                                    <div
                                                        key={test.id}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 10px',
                                                            background: '#f8fafc',
                                                            borderRadius: 6,
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                                            <Tag color={
                                                                test.tipus === 'es' ? 'blue' :
                                                                test.tipus === 'method' ? 'purple' :
                                                                test.tipus === 'methodtest' ? 'geekblue' : 'green'
                                                            } style={{ margin: 0, textTransform: 'uppercase', fontSize: 10 }}>
                                                                {test.tipus}
                                                            </Tag>
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                                                                {summary}
                                                            </span>
                                                            {details && (
                                                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                                                    ({details})
                                                                </span>
                                                            )}
                                                        </div>

                                                        <Space size="small">
                                                            {pasos.length > 1 && (
                                                                <Select
                                                                    size="small"
                                                                    value={test.ordre || (idx + 1)}
                                                                    style={{ width: 110 }}
                                                                    onChange={(newVal) => handleMoveTest(test, newVal)}
                                                                    options={pasos.map((_, pIdx) => ({
                                                                        value: pIdx + 1,
                                                                        label: `Mover a P${pIdx + 1}`
                                                                    }))}
                                                                />
                                                            )}
                                                            <Button
                                                                size="small"
                                                                type="text"
                                                                icon={<BiEdit />}
                                                                onClick={() => handleOpenEditTest(test)}
                                                                style={{ color: '#0284c7' }}
                                                            />
                                                            <Popconfirm
                                                                title="¿Eliminar este test?"
                                                                onConfirm={() => handleDeleteTest(test.id)}
                                                                okText="Sí"
                                                                cancelText="No"
                                                            >
                                                                <Button
                                                                    size="small"
                                                                    type="text"
                                                                    danger
                                                                    icon={<BiTrash />}
                                                                />
                                                            </Popconfirm>
                                                        </Space>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>

                <Divider style={{ margin: "20px 0" }} />

                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onClose}>Cancelar</Button>
                        <Button type="primary" htmlType="submit" size="large" style={{ paddingLeft: 32, paddingRight: 32, background: '#0284c7', borderColor: '#0284c7' }}>
                            {caza ? "Guardar Cambios" : "Crear Píldora"}
                        </Button>
                    </Space>
                </div>
            </Form>

            {/* Test Creation & Edition Sub-Modal */}
            <Modal
                title={editingTest ? `Editar Test - Paso ${targetStepOrder}` : `Nuevo Test - Paso ${targetStepOrder}`}
                open={testModalOpen}
                maskClosable={false}
                onCancel={() => {
                    setTestModalOpen(false);
                    setEditingTest(null);
                    testForm.resetFields();
                }}
                footer={null}
                width={700}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                        Tipo de Test:
                    </label>
                    <Select
                        value={testMode}
                        onChange={(val) => {
                            setTestMode(val);
                            if (!editingTest) testForm.resetFields();
                            testForm.setFieldsValue({ ordre: targetStepOrder });
                        }}
                        options={[
                            { value: "es", label: "Entrada y Salida estándar (E/S)" },
                            { value: "method", label: "Comprobar Firma de Método (Check Métode)" },
                            { value: "methodtest", label: "Comprobar Firma y Ejecución E/S Método" },
                            { value: "content", label: "Comprobar Presencia de Código / Palabra (Contenido)" },
                        ]}
                        style={{ width: "100%" }}
                        disabled={!!editingTest}
                    />
                </div>

                <Form
                    form={testForm}
                    onFinish={handleSaveTest}
                    layout="vertical"
                    initialValues={{ ordre: targetStepOrder }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Paso Asociado" name="ordre" rules={[{ required: true, message: 'Selecciona el paso' }]}>
                                <Select
                                    style={{ width: '100%' }}
                                    options={pasos.map((_, pIdx) => ({
                                        value: pIdx + 1,
                                        label: `Paso ${pIdx + 1}: ${pasos[pIdx]?.titulo || `Paso ${pIdx + 1}`}`
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Nombre del Test (Opcional)" name="name">
                                <Input placeholder={`Ej: Test validación paso ${targetStepOrder}`} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {testMode === "es" && (
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Entrada (Input)"
                                    name="entrada"
                                    rules={[{ required: !testSenseEntrada, message: "Indica la entrada o marca 'Sin entrada'" }]}
                                >
                                    <Input.TextArea
                                        rows={5}
                                        placeholder="Texto enviado a stdin..."
                                        disabled={testSenseEntrada}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Checkbox
                                        checked={testSenseEntrada}
                                        onChange={(e) => {
                                            setTestSenseEntrada(e.target.checked);
                                            if (e.target.checked) testForm.setFieldsValue({ entrada: "" });
                                        }}
                                    >
                                        Sin entrada estándar
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Salida Esperada (Output)"
                                    name="sortida"
                                    rules={[{ required: true, message: "Indica la salida esperada" }]}
                                >
                                    <Input.TextArea rows={5} placeholder="Texto esperado en stdout..." />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {(testMode === "method" || testMode === "methodtest") && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Nombre del Método" name="methodName" rules={[{ required: true, message: "Campo obligatorio" }]}>
                                        <Input placeholder="ej: suma, calcularTotal..." />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Tipo de Retorno" name="returnType" rules={[{ required: true, message: "Campo obligatorio" }]}>
                                        <Select options={testTypeOptions} showSearch placeholder="ej: int, void, String..." />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item label="Modificadores" name="modifiers">
                                <Select mode="multiple" options={testModifierOptions.map(o => ({ label: o, value: o }))} placeholder="public, static..." />
                            </Form.Item>

                            <Form.List name="parameters">
                                {(fields, { add, remove }) => (
                                    <>
                                        <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>Parámetros del Método:</div>
                                        {fields.map((field) => (
                                            <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'paramType']}
                                                    rules={[{ required: true, message: 'Falta el tipo' }]}
                                                >
                                                    <Select placeholder="Tipo" options={testTypeOptions} style={{ width: 120 }} showSearch />
                                                </Form.Item>
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, 'paramName']}
                                                    rules={[{ required: true, message: 'Falta el nombre' }]}
                                                >
                                                    <Input placeholder="Nombre" />
                                                </Form.Item>
                                                <BiTrash onClick={() => remove(field.name)} color="red" style={{ cursor: 'pointer', fontSize: 16 }} />
                                            </Space>
                                        ))}
                                        <Form.Item>
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                                Añadir Parámetro
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form.List>

                            {testMode === "methodtest" && (
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="entrada" label="Entrada"><Input.TextArea rows={3} /></Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name="sortida" label="Salida"><Input.TextArea rows={3} /></Form.Item>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}

                    {testMode === "content" && (
                        <>
                            <Form.Item label="Texto o Palabra Clave" name="contentText" rules={[{ required: true, message: "Indica el texto a comprobar" }]}>
                                <Input placeholder="ej: System.out.println, while, etc." />
                            </Form.Item>
                            <Form.Item name="mustContain" valuePropName="checked" initialValue={true}>
                                <Checkbox>El código del alumno DEBE contener este texto</Checkbox>
                            </Form.Item>
                        </>
                    )}

                    <Form.Item style={{ marginTop: 20, textAlign: 'right', marginBottom: 0 }}>
                        <Space>
                            <Button onClick={() => {
                                setTestModalOpen(false);
                                setEditingTest(null);
                                testForm.resetFields();
                            }}>
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={testSubmitting}
                                style={{ background: '#0284c7', borderColor: '#0284c7' }}
                            >
                                {editingTest ? "Guardar Test" : "Crear Test"}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {caza && (
                <PildoraRecompensesModal
                    visible={recompensesModalVisible}
                    onClose={() => setRecompensesModalVisible(false)}
                    caza={caza}
                    idConjunt={caza?.id_conjunt ?? defaultConjunt}
                />
            )}
        </Modal>
    );
};

export default NouCazaModal;
